// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Check,
  Loader2,
  RefreshCcw,
  ShieldCheck,
} from "lucide-react";
import {
  buildCorepKcpReturnContext,
  clearCorepKcpReturnContext,
  loadCorepKcpReturnContext,
  normalizeCorepIdentityStatus,
  saveCorepKcpReturnContext,
  validateCorepKcpStatusForInvite,
} from "./corepKcpReturnContext";

const SUPPORT_PHONE = "010-9988-8639";
const COREP_KCP_WINDOW_NAME = "cnfood_corep_kcp";
const CONTRACT_REVIEW_NOT_READY_MESSAGE =
  "계약 내용을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.";
const COREP_KCP_STATUS_POLL_DELAYS_MS = [0, 2000, 3000, 5000, 8000, 13000, 21000];
const COREP_KCP_STATUS_CHECKING_MESSAGE =
  "본인확인 결과를 확인하고 있습니다. 잠시만 기다려 주세요.";
const COREP_KCP_STATUS_DELAYED_MESSAGE =
  "본인확인 결과 확인이 지연되고 있습니다. 잠시 후 상태 새로고침을 눌러 주세요.";

const normalizePhoneDigits = (value = "") =>
  String(value || "").replace(/[^0-9]/g, "");

const formatPhoneNumberForDisplay = (value = "") => {
  const digits = normalizePhoneDigits(value).slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
};

const isValidPhoneNumberFormat = (value = "") =>
  /^01[016789]\d{7,8}$/.test(normalizePhoneDigits(value));

const sanitizeNumericInput = (value = "", maxLength = 0) => {
  const digits = String(value || "").replace(/[^0-9]/g, "");
  return maxLength ? digits.slice(0, maxLength) : digits;
};

const createSha256Hash = async (value = "") => {
  const buffer = new TextEncoder().encode(String(value || ""));
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
};

const getCanvasSignatureMetrics = (canvas) => {
  if (!canvas) {
    return { inkPixels: 0, boundingWidth: 0, boundingHeight: 0, inkRatio: 0 };
  }
  const context = canvas.getContext("2d");
  const image = context.getImageData(0, 0, canvas.width, canvas.height);
  let minX = canvas.width;
  let minY = canvas.height;
  let maxX = 0;
  let maxY = 0;
  let inkPixels = 0;
  for (let index = 0; index < image.data.length; index += 4) {
    const alpha = image.data[index + 3];
    const red = image.data[index];
    const green = image.data[index + 1];
    const blue = image.data[index + 2];
    if (alpha > 20 && red + green + blue < 735) {
      const pixel = index / 4;
      const x = pixel % canvas.width;
      const y = Math.floor(pixel / canvas.width);
      inkPixels += 1;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }
  const boundingWidth = inkPixels ? maxX - minX + 1 : 0;
  const boundingHeight = inkPixels ? maxY - minY + 1 : 0;
  return {
    inkPixels,
    boundingWidth,
    boundingHeight,
    inkRatio: inkPixels / Math.max(canvas.width * canvas.height, 1),
  };
};

const validateSignatureQualityMetrics = (metrics = {}) => {
  if (!metrics.inkPixels || metrics.inkPixels < 80) {
    return { valid: false, reason: "TOO_SMALL", ...metrics };
  }
  if (metrics.boundingWidth < 40 || metrics.boundingHeight < 20) {
    return { valid: false, reason: "TOO_SHORT", ...metrics };
  }
  return { valid: true, reason: "", ...metrics };
};

const getSignatureQualityErrorMessage = (reason = "") => {
  if (reason === "TOO_SHORT") return "점이나 짧은 선은 서명으로 인정되지 않습니다.";
  return "서명란 안에 이름을 알아볼 수 있도록 다시 작성해 주세요.";
};

const getCanvasDataUrlWithWhiteBackground = (canvas) => {
  const output = document.createElement("canvas");
  output.width = canvas.width;
  output.height = canvas.height;
  const context = output.getContext("2d");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, output.width, output.height);
  context.drawImage(canvas, 0, 0);
  return output.toDataURL("image/png");
};

const buildKcpIdentityFormFields = ({ regCertKey = "", pageSubmitYn = "Y" }) => [
  { name: "reg_cert_key", value: regCertKey },
  { name: "page_submit_yn", value: pageSubmitYn },
];

const submitKcpIdentityForm = ({
  callUrl = "",
  regCertKey = "",
  pageSubmitYn = "Y",
  target = "_self",
}) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = callUrl;
  form.target = target || "_self";
  form.style.display = "none";
  buildKcpIdentityFormFields({ regCertKey, pageSubmitYn }).forEach((field) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = field.name;
    input.value = field.value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
  form.remove();
};

const openCorepKcpAuthWindow = () => {
  try {
    const popup = window.open("", COREP_KCP_WINDOW_NAME, "width=430,height=720");
    if (popup?.document) {
      popup.document.title = "CNFOOD KCP";
      popup.document.body.innerHTML =
        '<p style="font-family: sans-serif; padding: 24px;">본인확인 창을 여는 중입니다.</p>';
    }
    return popup;
  } catch (error) {
    return null;
  }
};

const normalizeContractClauses = (snapshot = {}) => {
  const rawClauses = Array.isArray(snapshot)
    ? snapshot
    : Array.isArray(snapshot?.clauses)
    ? snapshot.clauses
    : [];
  return rawClauses
    .map((item) => ({
      title: String(item?.title || ""),
      body: String(item?.body || item?.content || ""),
    }))
    .filter((item) => item.title || item.body);
};

const EMPTY_CONTRACT_CLAUSES_SNAPSHOT = {
  title: "",
  version: "",
  clauses: [],
};

export const resolveCorepContractClausesSnapshot = (contract = {}) => {
  const sources = [
    contract?.contractClausesSnapshot,
    contract?.finalSnapshot?.contractClausesSnapshot,
    contract?.contractTemplateSnapshot,
    contract?.finalSnapshot?.contractTemplateSnapshot,
    contract?.contractTemplate,
    contract?.finalSnapshot?.contractTemplate,
  ];
  const snapshot =
    sources.find((candidate) => {
      if (Array.isArray(candidate)) return candidate.length > 0;
      return Array.isArray(candidate?.clauses) && candidate.clauses.length > 0;
    }) ||
    contract?.contractClausesSnapshot ||
    contract?.contractTemplateSnapshot ||
    EMPTY_CONTRACT_CLAUSES_SNAPSHOT;
  const clauses = normalizeContractClauses(snapshot);
  return {
    title: String(
      (Array.isArray(snapshot) ? "" : snapshot?.title) ||
        contract?.contractTemplateTitle ||
        contract?.contractTitle ||
        ""
    ),
    version: String(
      (Array.isArray(snapshot) ? "" : snapshot?.version) ||
        contract?.contractTemplateVersion ||
        contract?.finalSnapshot?.contractTemplateVersion ||
        ""
    ),
    clauses,
  };
};

const hasCorepContractReviewContent = ({ contract = {}, clauses = [] } = {}) =>
  Boolean(
    (contract.contractNumber ||
      contract.businessName ||
      contract.principalRepresentativeName) &&
      Array.isArray(clauses) &&
      clauses.length > 0
  );

const formatEvidenceDate = (value = "") => {
  if (!value) return "미수집";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("ko-KR");
};

export default function CorepInviteFlow({
  token = "",
  tokenHash = "",
  signatureOnly = false,
  appId = "cnfood-final-v48",
}) {
  const canvasRef = useRef(null);
  const corepReturnContextSyncedRef = useRef(false);
  const corepKcpPollRunRef = useRef(0);
  const [corepStep, setCorepStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [corepError, setCorepError] = useState("");
  const [summary, setSummary] = useState(null);
  const [corepForm, setCorepForm] = useState({
    ssnFront: "",
    ssnBack: "",
    phone: "",
    homeAddress: "",
    homeAddressDetail: "",
    contractChecked: false,
  });
  const [corepKcpSessionId, setCorepKcpSessionId] = useState("");
  const [corepKcpOrderId, setCorepKcpOrderId] = useState("");
  const [corepKcpStatus, setCorepKcpStatus] = useState(null);
  const [corepKcpPollingState, setCorepKcpPollingState] = useState("IDLE");
  const [corepSignatureError, setCorepSignatureError] = useState("");
  const [corepCompletedResult, setCorepCompletedResult] = useState(null);

  const coRepresentative = summary?.coRepresentative || {};
  const contract = summary?.contract || {};
  const corepSignatureOnly = signatureOnly === true;
  const effectiveCorepInviteTokenHash =
    tokenHash || summary?.coRepresentative?.inviteTokenHash || "";
  const contractClausesSnapshot = useMemo(
    () => resolveCorepContractClausesSnapshot(contract),
    [contract]
  );
  const contractClauses = Array.isArray(contractClausesSnapshot?.clauses)
    ? contractClausesSnapshot.clauses
    : [];
  const canShowContractReview = hasCorepContractReviewContent({
    contract,
    clauses: contractClauses,
  });
  const corepIdentityDisplay = normalizeCorepIdentityStatus({
    status: corepKcpStatus,
    coRepresentativeId: coRepresentative.id,
  });
  const corepIdentityMatched = corepIdentityDisplay.canProceed === true;
  const isCorepKcpChecking = corepKcpPollingState === "CHECKING";
  const corepIdentityToneClass =
    corepIdentityDisplay.tone === "green"
      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
      : corepIdentityDisplay.tone === "red"
      ? "border-red-200 bg-red-50 text-red-700"
      : corepIdentityDisplay.tone === "amber"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : corepIdentityDisplay.tone === "blue"
      ? "border-blue-200 bg-blue-50 text-blue-800"
      : "border-slate-200 bg-slate-50 text-slate-700";

  const postJson = async (url, body) => {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data?.ok === false) {
      throw new Error(data?.userMessage || data?.message || "요청을 처리하지 못했습니다.");
    }
    return data;
  };

  const corepGoToStep = (nextStep) => {
    setCorepStep(Math.max(0, Math.min(5, Number(nextStep || 0))));
    window.scrollTo(0, 0);
  };

  const goToContractReview = () => {
    if (!canShowContractReview) {
      setCorepError(CONTRACT_REVIEW_NOT_READY_MESSAGE);
      return;
    }
    corepGoToStep(3);
  };

  useEffect(() => {
    let cancelled = false;
    const loadInvite = async () => {
      setLoading(true);
      setCorepError("");
      if (!token && !tokenHash) {
        setCorepError("유효하지 않은 공동계약인 링크입니다. 본사에 다시 요청해 주세요.");
        setLoading(false);
        return;
      }
      try {
        const data = await postJson("/api/admin-login", {
          mode: "getCoRepInvite",
          appId,
          token,
          tokenHash,
        });
        if (!data?.ok || !data?.contract || !data?.coRepresentative) {
          throw new Error(
            "공동계약인 계약 정보를 찾을 수 없습니다. 본사에 다시 요청해 주세요."
          );
        }
        if (!cancelled) {
          setSummary(data);
          const loadedRepresentative = data?.coRepresentative || {};
          const restoredSessionId =
            loadedRepresentative.latestKcpSessionId ||
            loadedRepresentative.pendingKcpSessionId ||
            loadedRepresentative.kcpSessionId ||
            "";
          if (restoredSessionId) setCorepKcpSessionId(restoredSessionId);
          if (loadedRepresentative.latestKcpOrderId) {
            setCorepKcpOrderId(loadedRepresentative.latestKcpOrderId);
          }
          if (loadedRepresentative.identityVerification) {
            setCorepKcpStatus({
              ...loadedRepresentative.identityVerification,
              status:
                loadedRepresentative.identityVerification.status ||
                loadedRepresentative.verificationStatus ||
                "PENDING",
              subjectType: "CO_REP",
              coRepresentativeId: loadedRepresentative.id || "",
              hasCi: loadedRepresentative.hasCi === true,
              hasDi: loadedRepresentative.hasDi === true,
            });
          } else if (loadedRepresentative.kcpStatus) {
            setCorepKcpStatus({
              status: loadedRepresentative.kcpStatus,
              subjectType: "CO_REP",
              coRepresentativeId: loadedRepresentative.id || "",
              hasCi: loadedRepresentative.hasCi === true,
              hasDi: loadedRepresentative.hasDi === true,
            });
          }
          if (corepSignatureOnly) {
            setCorepForm((prev) => ({ ...prev, contractChecked: true }));
            corepGoToStep(4);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setCorepError(
            error?.message ||
              "공동계약인 인증 링크를 확인하지 못했습니다. 본사에 다시 요청해 주세요."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadInvite();
    return () => {
      cancelled = true;
    };
  }, [appId, token, tokenHash, corepSignatureOnly]);

  const updateCorepField = (field, value) => {
    setCorepForm((prev) => ({
      ...prev,
      [field]:
        field === "ssnFront"
          ? sanitizeNumericInput(value, 6)
          : field === "ssnBack"
          ? sanitizeNumericInput(value, 7)
          : field === "phone"
          ? formatPhoneNumberForDisplay(value)
          : value,
    }));
    setCorepError("");
  };

  const validatePrivateInfo = () => {
    if (corepForm.ssnFront.length !== 6) return "주민번호 앞 6자리를 입력해 주세요.";
    if (corepForm.ssnBack.length !== 7) return "주민번호 뒷 7자리를 입력해 주세요.";
    if (!isValidPhoneNumberFormat(corepForm.phone)) {
      return "본인 명의 휴대폰번호를 입력해 주세요.";
    }
    if (!String(corepForm.homeAddress || "").trim()) {
      return "공동계약인 자택주소를 입력해 주세요.";
    }
    return "";
  };

  const openCorepAddressSearch = () => {
    if (!window.daum?.Postcode) {
      setToastMessage("주소검색 창을 열 수 없습니다. 주소를 직접 입력해 주세요.");
      return;
    }
    new window.daum.Postcode({
      oncomplete: (data) => {
        updateCorepField("homeAddress", data.roadAddress || data.jibunAddress || "");
      },
    }).open();
  };

  const startCorepKcp = async () => {
    const validationMessage = validatePrivateInfo();
    if (validationMessage) {
      setCorepError(validationMessage);
      return;
    }
    const corepKcpPopup = openCorepKcpAuthWindow();
    const corepKcpWindowTarget = corepKcpPopup ? COREP_KCP_WINDOW_NAME : "_self";
    setSubmitting(true);
    setCorepError("");
    try {
      const data = await postJson("/api/kcp/identity/start", {
        appId,
        contractDraftId: `corep_${contract.documentId || contract.contractId}_${coRepresentative.id}`,
        subjectType: "CO_REP",
        coRepresentativeId: coRepresentative.id,
        corepInviteToken: token,
        customerName: contract.businessName || "",
        representativeName: coRepresentative.name || "",
        representativeBirthDate: corepForm.ssnFront,
        representativePhone: corepForm.phone,
        phone: corepForm.phone,
        returnPath: `${window.location.pathname}${window.location.search}`,
      });
      setCorepKcpSessionId(data.sessionId || "");
      setCorepKcpOrderId(data.orderId || "");
      saveCorepKcpReturnContext(
        await buildCorepKcpReturnContext({
          corepInviteToken: token,
          corepInviteTokenHash: data.corepInviteTokenHash || "",
          coRepresentativeId: coRepresentative.id,
          kcpSessionId: data.sessionId || "",
          orderId: data.orderId || "",
        })
      );
      setCorepKcpStatus({
        status: data.status || "REGISTERED",
        subjectType: "CO_REP",
        coRepresentativeId: coRepresentative.id,
      });
      if (data.callUrl && data.regCertKey) {
        submitKcpIdentityForm({
          callUrl: data.callUrl,
          regCertKey: data.regCertKey,
          pageSubmitYn: data.pageSubmitYn || "Y",
          target: corepKcpWindowTarget,
        });
      } else if (corepKcpPopup && !corepKcpPopup.closed) {
        corepKcpPopup.close();
      }
      void pollCorepKcpStatus(data.sessionId || "");
      setToastMessage("본인확인 창에서 인증을 완료한 뒤 상태 새로고침을 눌러 주세요.");
    } catch (error) {
      if (corepKcpPopup && !corepKcpPopup.closed) {
        corepKcpPopup.close();
      }
      setCorepError(error?.message || "공동계약인 본인확인을 시작하지 못했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const refreshCorepKcpStatus = async (
    sessionIdOverride = "",
    { source = "manual", suppressError = false } = {}
  ) => {
    const context = loadCorepKcpReturnContext();
    const targetSessionId =
      sessionIdOverride ||
      corepKcpSessionId ||
      context?.kcpSessionId ||
      coRepresentative.latestKcpSessionId ||
      coRepresentative.pendingKcpSessionId ||
      coRepresentative.kcpSessionId ||
      "";
    const targetOrderId =
      corepKcpOrderId || context?.orderId || coRepresentative.latestKcpOrderId || "";
    const targetTokenHash =
      effectiveCorepInviteTokenHash || context?.corepInviteTokenHash || "";
    setSubmitting(true);
    setCorepKcpPollingState("CHECKING");
    setCorepError("");
    try {
      const data = await postJson("/api/admin-login", {
        mode: "resolveCoRepKcpStatus",
        appId,
        token,
        tokenHash: targetTokenHash,
        coRepresentativeId: coRepresentative.id,
        kcpSessionId: targetSessionId,
        orderId: targetOrderId,
      });
      const corepStatusDecision = validateCorepKcpStatusForInvite({
        status: data,
        coRepresentativeId: coRepresentative.id,
      });
      if (!corepStatusDecision.ok) {
        setCorepKcpPollingState("MISMATCHED");
        if (suppressError) {
          return { ok: false, verified: false, data, code: corepStatusDecision.code };
        }
        setCorepError(
          "공동계약인 본인확인이 완료되지 않았습니다. 공동계약인 본인 명의 휴대폰으로 본인확인을 완료해 주세요. 본인확인 정보가 입력한 공동계약인 정보와 일치해야 다음 단계로 진행할 수 있습니다."
        );
        return { ok: false, verified: false, data, code: corepStatusDecision.code };
      }
      setCorepKcpStatus(data);
      if (data.sessionId) setCorepKcpSessionId(data.sessionId);
      if (data.orderId) setCorepKcpOrderId(data.orderId);
      if (corepStatusDecision.verified) {
        setCorepKcpPollingState("VERIFIED");
        setToastMessage("공동계약인 본인확인이 완료되었습니다.");
        if (canShowContractReview) {
          corepGoToStep(3);
        } else {
          setCorepError(CONTRACT_REVIEW_NOT_READY_MESSAGE);
        }
        return { ok: true, verified: true, data };
      } else {
        setCorepKcpPollingState(data.status || "REGISTERED");
        if (source !== "manual") {
          return { ok: true, verified: false, data };
        }
        setToastMessage("본인확인 완료 상태를 아직 확인하지 못했습니다.");
        return { ok: true, verified: false, data };
      }
    } catch (error) {
      setCorepKcpPollingState("STATUS_NOT_FOUND");
      if (suppressError) {
        return { ok: false, verified: false, code: "STATUS_NOT_FOUND", error };
      }
      setCorepError(error?.message || "본인확인 상태 확인에 실패했습니다.");
      return { ok: false, verified: false, code: "STATUS_NOT_FOUND", error };
    } finally {
      setSubmitting(false);
    }
  };

  const pollCorepKcpStatus = async (sessionIdOverride = "") => {
    const runId = Date.now();
    corepKcpPollRunRef.current = runId;
    setCorepKcpPollingState("CHECKING");
    setToastMessage(COREP_KCP_STATUS_CHECKING_MESSAGE);
    for (const delayMs of COREP_KCP_STATUS_POLL_DELAYS_MS) {
      if (delayMs > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, delayMs));
      }
      if (corepKcpPollRunRef.current !== runId) return;
      const result = await refreshCorepKcpStatus(sessionIdOverride, {
        source: "auto",
        suppressError: true,
      });
      if (result?.verified || corepKcpPollRunRef.current !== runId) return;
    }
    if (corepKcpPollRunRef.current === runId) {
      setCorepKcpPollingState("STATUS_NOT_FOUND");
      setCorepError(COREP_KCP_STATUS_DELAYED_MESSAGE);
    }
  };

  useEffect(() => {
    if (!summary || corepReturnContextSyncedRef.current) return;
    const context = loadCorepKcpReturnContext();
    if (!context) return;
    if (context.corepInviteToken && token && context.corepInviteToken !== token) return;
    if (
      context.corepInviteTokenHash &&
      tokenHash &&
      context.corepInviteTokenHash !== tokenHash
    ) {
      return;
    }
    if (
      context.coRepresentativeId &&
      coRepresentative.id &&
      context.coRepresentativeId !== coRepresentative.id
    ) {
      setCorepError(
        "공동계약인 본인확인이 완료되지 않았습니다. 공동계약인 본인 명의 휴대폰으로 본인확인을 완료해 주세요. 본인확인 정보가 입력한 공동계약인 정보와 일치해야 다음 단계로 진행할 수 있습니다."
      );
      return;
    }
    corepReturnContextSyncedRef.current = true;
    setCorepKcpSessionId(context.kcpSessionId || "");
    setCorepKcpOrderId(context.orderId || "");
    if (context.kcpSessionId) {
      corepGoToStep(2);
      void pollCorepKcpStatus(context.kcpSessionId);
    }
  }, [summary, token, tokenHash, coRepresentative.id]);

  useEffect(() => {
    if (!summary || corepIdentityMatched || corepStep >= 3) return;
    const hasPendingKcpContext = Boolean(
      corepKcpSessionId ||
        corepKcpOrderId ||
        coRepresentative.latestKcpSessionId ||
        coRepresentative.pendingKcpSessionId ||
        loadCorepKcpReturnContext()
    );
    if (!hasPendingKcpContext) return;
    const sync = () => {
      if (document.visibilityState && document.visibilityState !== "visible") return;
      void pollCorepKcpStatus(corepKcpSessionId);
    };
    window.addEventListener("pageshow", sync);
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", sync);
    return () => {
      window.removeEventListener("pageshow", sync);
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", sync);
    };
  }, [
    summary,
    corepIdentityMatched,
    corepStep,
    corepKcpSessionId,
    corepKcpOrderId,
    coRepresentative.latestKcpSessionId,
    coRepresentative.pendingKcpSessionId,
  ]);

  const getCanvasPosition = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const pointer = event.touches?.[0] || event;
    return {
      x: ((pointer.clientX - rect.left) / rect.width) * canvas.width,
      y: ((pointer.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const drawSignature = (event, start = false) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    event.preventDefault();
    const context = canvas.getContext("2d");
    const point = getCanvasPosition(event);
    context.lineWidth = 4;
    context.lineCap = "round";
    context.strokeStyle = "#0f172a";
    if (start) {
      context.beginPath();
      context.moveTo(point.x, point.y);
      canvas.dataset.drawing = "true";
    } else if (canvas.dataset.drawing === "true") {
      context.lineTo(point.x, point.y);
      context.stroke();
    }
    setCorepSignatureError("");
  };

  const stopSignature = () => {
    if (canvasRef.current) canvasRef.current.dataset.drawing = "false";
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setCorepSignatureError("");
  };

  const submitCorepSignature = async () => {
    if (!corepSignatureOnly && !corepIdentityMatched) {
      setCorepError("공동계약인 본인확인을 먼저 완료해 주세요.");
      corepGoToStep(2);
      return;
    }
    if (!corepSignatureOnly && (!canShowContractReview || !corepForm.contractChecked)) {
      setCorepError(CONTRACT_REVIEW_NOT_READY_MESSAGE);
      corepGoToStep(3);
      return;
    }
    const validation = validateSignatureQualityMetrics(
      canvasRef.current ? getCanvasSignatureMetrics(canvasRef.current) : {}
    );
    if (!canvasRef.current || !validation.valid) {
      setCorepSignatureError(getSignatureQualityErrorMessage(validation.reason));
      return;
    }
    setSubmitting(true);
    setCorepError("");
    try {
      const imageDataUrl = getCanvasDataUrlWithWhiteBackground(canvasRef.current);
      const signatureHash = await createSha256Hash(imageDataUrl);
      const signedAt = new Date().toISOString();
      const result = await postJson("/api/admin-login", {
        mode: corepSignatureOnly
          ? "submitCoRepSignatureImageOnly"
          : "submitCoRepSignature",
        appId,
        token,
        tokenHash: effectiveCorepInviteTokenHash,
        ...(corepSignatureOnly
          ? {}
          : {
              kcpSessionId: corepKcpSessionId,
              orderId: corepKcpOrderId,
              privateInfo: {
                ssnFront: corepForm.ssnFront,
                ssnBack: corepForm.ssnBack,
                homeAddress: corepForm.homeAddress,
                homeAddressDetail: corepForm.homeAddressDetail,
              },
            }),
        signatureEvidence: {
          role: corepSignatureOnly
            ? "CO_REPRESENTATIVE_SIGNATURE_IMAGE_REFRESH"
            : "CO_REPRESENTATIVE_DIRECT",
          label: corepSignatureOnly
            ? "공동계약인 서명 이미지 재등록"
            : "공동계약인 직접서명",
          signerName: coRepresentative.name || "",
          valid: true,
          validation,
          signatureHash,
          signatureImageDataUrl: imageDataUrl,
          signedAt,
        },
      });
      setCorepCompletedResult(result);
      clearCorepKcpReturnContext();
      corepGoToStep(5);
    } catch (error) {
      setCorepError(error?.message || "공동계약인 서명을 저장하지 못했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-5">
        <div className="mx-auto flex min-h-[70vh] max-w-xl flex-col items-center justify-center text-center">
          <Loader2 className="mb-4 h-10 w-10 animate-spin text-red-600" />
          <p className="text-base font-black text-slate-700">공동계약인 인증 링크를 확인하고 있습니다.</p>
        </div>
      </div>
    );
  }

  if (corepError && !summary) {
    return (
      <div className="min-h-screen bg-slate-50 p-5">
        <div className="mx-auto max-w-xl rounded-2xl border border-red-200 bg-white p-6 shadow-sm">
          <AlertTriangle className="mb-3 h-8 w-8 text-red-600" />
          <h1 className="text-xl font-black text-slate-900">공동계약인 링크를 확인할 수 없습니다</h1>
          <p className="mt-3 break-keep text-base font-bold leading-7 text-slate-600">{corepError}</p>
          <a
            href={`tel:${SUPPORT_PHONE.replace(/-/g, "")}`}
            className="mt-5 flex min-h-[52px] items-center justify-center rounded-2xl bg-slate-900 px-4 py-3 text-base font-black text-white"
          >
            문의 {SUPPORT_PHONE}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-5 text-slate-900">
      <div className="mx-auto max-w-2xl">
        <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-xs font-black uppercase tracking-wider text-red-600">CN FOOD CO-REPRESENTATIVE</p>
          <h1 className="mt-2 break-keep text-2xl font-black leading-snug">
            {corepSignatureOnly
              ? "공동계약인 서명 이미지 등록"
              : "공동계약인 본인확인 및 전자서명"}
          </h1>
          <p className="mt-2 break-keep text-sm font-bold leading-6 text-slate-600">
            {corepSignatureOnly
              ? "공동계약인 서명만 다시 등록합니다. 본인의 성명을 직접 작성해 주세요."
              : "주대표자가 작성한 계약 내용을 확인한 뒤, 공동계약인 본인 명의 휴대폰으로 본인확인과 직접 전자서명을 완료해 주세요."}
          </p>
        </div>

        {toastMessage && (
          <div className="mb-4 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm font-bold leading-6 text-blue-800">
            {toastMessage}
          </div>
        )}
        {corepError && (
          <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-bold leading-6 text-red-700">
            {corepError}
          </div>
        )}

        <div
          className={`mb-4 grid gap-1 text-center text-[11px] font-black text-slate-500 ${
            corepSignatureOnly ? "grid-cols-3" : "grid-cols-5"
          }`}
        >
          {(corepSignatureOnly
            ? ["확인", "서명", "완료"]
            : ["요약", "정보", "본인확인", "계약확인", "서명"]
          ).map((label, index) => (
            <div key={label} className={`rounded-full px-2 py-2 ${corepStep >= index ? "bg-slate-900 text-white" : "bg-white"}`}>
              {label}
            </div>
          ))}
        </div>

        {corepStep === 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-black">계약 기본정보</h2>
            <div className="mt-4 space-y-2 text-sm font-bold">
              {[
                ["계약번호", contract.contractNumber || contract.contractId || "미수집"],
                ["상호명", contract.businessName || "미수집"],
                ["사업자번호", contract.businessNumberMasked || "미수집"],
                ["주대표자", contract.principalRepresentativeName || "미수집"],
                ["공동계약인", coRepresentative.name || "미수집"],
                ["휴대폰", coRepresentative.phoneMasked || "미수집"],
                ["주대표 작성일", formatEvidenceDate(contract.primaryCompletedAt)],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between gap-3 border-b border-slate-100 pb-2">
                  <span className="shrink-0 text-slate-500">{label}</span>
                  <span className="min-w-0 break-words text-right text-slate-900">{value}</span>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => corepGoToStep(1)} className="mt-5 flex min-h-[56px] w-full items-center justify-center rounded-2xl bg-red-600 px-4 py-3 text-base font-black text-white">
              공동계약인 확인 시작
            </button>
          </div>
        )}

        {corepStep === 1 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-black">공동계약인 본인정보 입력</h2>
            <p className="mt-2 break-keep text-sm font-bold leading-6 text-slate-600">
              주민번호와 주소는 공동계약인 본인이 직접 입력합니다. 입력값은 일반 화면에 전체 노출되지 않습니다.
            </p>
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-black">주민번호 앞 6자리</span>
                <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} value={corepForm.ssnFront} onChange={(event) => updateCorepField("ssnFront", event.target.value)} className="min-h-[54px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-lg font-black outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100" />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-black">주민번호 뒷 7자리</span>
                <input type="password" inputMode="numeric" pattern="[0-9]*" maxLength={7} value={corepForm.ssnBack} onChange={(event) => updateCorepField("ssnBack", event.target.value)} className="min-h-[54px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-lg font-black outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100" />
              </label>
            </div>
            <label className="mt-3 block">
              <span className="mb-2 block text-sm font-black">본인 명의 휴대폰번호</span>
              <input type="tel" inputMode="numeric" value={corepForm.phone} onChange={(event) => updateCorepField("phone", event.target.value)} placeholder="010-0000-0000" className="min-h-[54px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-lg font-black outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100" />
            </label>
            <label className="mt-3 block">
              <span className="mb-2 block text-sm font-black">자택주소</span>
              <div className="flex gap-2">
                <input type="text" value={corepForm.homeAddress} onChange={(event) => updateCorepField("homeAddress", event.target.value)} className="min-h-[54px] min-w-0 flex-1 rounded-2xl border border-slate-200 px-4 py-3 text-base font-bold outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100" />
                <button type="button" onClick={openCorepAddressSearch} className="shrink-0 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-black text-white">주소검색</button>
              </div>
            </label>
            <label className="mt-3 block">
              <span className="mb-2 block text-sm font-black">상세주소</span>
              <input type="text" value={corepForm.homeAddressDetail} onChange={(event) => updateCorepField("homeAddressDetail", event.target.value)} className="min-h-[54px] w-full rounded-2xl border border-slate-200 px-4 py-3 text-base font-bold outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100" />
            </label>
            <button type="button" onClick={() => {
              const validationMessage = validatePrivateInfo();
              if (validationMessage) {
                setCorepError(validationMessage);
                return;
              }
              corepGoToStep(2);
            }} className="mt-5 flex min-h-[56px] w-full items-center justify-center rounded-2xl bg-red-600 px-4 py-3 text-base font-black text-white">
              다음
            </button>
          </div>
        )}

        {corepStep === 2 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-black">공동계약인 KCP/PASS 본인확인</h2>
            <p className="mt-2 break-keep text-sm font-bold leading-6 text-slate-600">
              공동계약인 본인 명의 휴대폰으로 인증해야 합니다. 주대표 인증이나 다른 공동계약인 인증은 사용할 수 없습니다.
            </p>
            <div className={`mt-4 rounded-2xl border p-4 text-sm font-bold leading-6 ${corepIdentityToneClass}`}>
              상태: {corepIdentityDisplay.displayLabel}
              <p className="mt-2 break-keep text-sm leading-6">
                {isCorepKcpChecking
                  ? COREP_KCP_STATUS_CHECKING_MESSAGE
                  : corepIdentityDisplay.description}
              </p>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <button type="button" onClick={startCorepKcp} disabled={submitting || corepIdentityMatched} className="flex min-h-[54px] items-center justify-center gap-2 rounded-2xl bg-red-600 px-4 py-3 text-base font-black text-white disabled:bg-slate-300">
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShieldCheck className="h-5 w-5" />}
                {corepIdentityMatched ? "본인확인 완료" : "본인확인 시작"}
              </button>
              <button type="button" onClick={refreshCorepKcpStatus} disabled={submitting || !coRepresentative.id} className="flex min-h-[54px] items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-base font-black text-slate-800 disabled:opacity-50">
                <RefreshCcw className="h-5 w-5" /> 상태 새로고침
              </button>
            </div>
            {corepIdentityMatched && (
              <button type="button" onClick={goToContractReview} disabled={!canShowContractReview} className="mt-4 flex min-h-[54px] w-full items-center justify-center rounded-2xl bg-slate-900 px-4 py-3 text-base font-black text-white disabled:bg-slate-300">
                계약 내용 확인으로 이동
              </button>
            )}
          </div>
        )}

        {corepStep === 3 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-black">계약 내용 확인</h2>
            <p className="mt-2 break-keep text-sm font-bold leading-6 text-slate-600">계약 내용은 주대표자가 작성한 내용이며 이 화면에서 수정할 수 없습니다.</p>
            {!canShowContractReview && (
              <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-bold leading-6 text-amber-800">
                {CONTRACT_REVIEW_NOT_READY_MESSAGE}
              </div>
            )}
            <div className="mt-4 max-h-[45vh] overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 text-sm font-bold leading-6 text-slate-700">
                <p>계약번호: {contract.contractNumber || contract.contractId || "확인 중"}</p>
                <p>상호명: {contract.businessName || "확인 중"}</p>
                <p>사업자번호: {contract.businessNumberMasked || "확인 중"}</p>
                <p>주대표자명: {contract.principalRepresentativeName || "확인 중"}</p>
                <p>공동계약인: {coRepresentative.name || "확인 중"} / {coRepresentative.phoneMasked || "확인 중"}</p>
              </div>
              {contractClauses.length > 0 ? contractClauses.map((clause, index) => (
                <section key={`${clause.title || index}`} className="mb-4 last:mb-0">
                  <h3 className="text-base font-black text-slate-900">{clause.title || `조항 ${index + 1}`}</h3>
                  <p className="mt-2 whitespace-pre-wrap break-keep text-sm font-bold leading-7 text-slate-700">{clause.body || "조항 내용이 없습니다."}</p>
                </section>
              )) : <p className="text-sm font-bold text-slate-500">계약 조항을 불러오지 못했습니다.</p>}
            </div>
            <label className="mt-4 flex items-start gap-3 rounded-2xl border border-slate-200 p-4 text-sm font-black leading-6">
              <input type="checkbox" checked={corepForm.contractChecked} onChange={(event) => updateCorepField("contractChecked", event.target.checked)} className="mt-1 h-5 w-5" />
              계약 내용을 읽고 공동계약인 본인확인 및 전자서명을 진행합니다.
            </label>
            <button type="button" onClick={() => {
              if (!canShowContractReview) {
                setCorepError(CONTRACT_REVIEW_NOT_READY_MESSAGE);
                return;
              }
              if (!corepForm.contractChecked) {
                setCorepError("계약 내용을 확인했다는 체크가 필요합니다.");
                return;
              }
              corepGoToStep(4);
            }} disabled={!canShowContractReview || !corepForm.contractChecked} className="mt-5 flex min-h-[56px] w-full items-center justify-center rounded-2xl bg-red-600 px-4 py-3 text-base font-black text-white disabled:bg-slate-300">
              서명하러 가기
            </button>
          </div>
        )}

        {corepStep === 4 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="text-xl font-black">
              {corepSignatureOnly ? "공동계약인 서명 재등록" : "공동계약인 직접서명"}
            </h2>
            <p className="mt-2 break-keep text-sm font-bold leading-6 text-slate-600">
              {corepSignatureOnly
                ? "공동계약인 서명만 다시 등록합니다. 본인의 성명을 정자로 작성해 주세요."
                : "본인의 성명을 정자로 작성해 주세요. 점이나 짧은 선은 서명으로 인정되지 않습니다."}
            </p>
            <div className="relative mt-4 rounded-2xl border-2 border-slate-200 bg-white">
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-4xl font-black text-slate-200">{coRepresentative.name || "공동계약인"}</div>
              <canvas ref={canvasRef} width={640} height={220} onMouseDown={(event) => drawSignature(event, true)} onMouseMove={(event) => drawSignature(event)} onMouseUp={stopSignature} onMouseLeave={stopSignature} onTouchStart={(event) => drawSignature(event, true)} onTouchMove={(event) => drawSignature(event)} onTouchEnd={stopSignature} className="relative z-10 h-[220px] w-full touch-none" />
            </div>
            {corepSignatureError && (
              <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-bold leading-6 text-red-700">{corepSignatureError}</div>
            )}
            <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <button type="button" onClick={clearSignature} className="min-h-[52px] rounded-2xl border border-slate-200 px-4 py-3 text-base font-black text-slate-700">다시 서명하기</button>
              <button type="button" onClick={submitCorepSignature} disabled={submitting} className="flex min-h-[52px] items-center justify-center gap-2 rounded-2xl bg-red-600 px-4 py-3 text-base font-black text-white disabled:bg-slate-300">
                {submitting && <Loader2 className="h-5 w-5 animate-spin" />}
                공동계약인 서명 완료
              </button>
            </div>
          </div>
        )}

        {corepStep === 5 && (
          <div className="rounded-2xl border border-emerald-200 bg-white p-5 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500 text-white">
              <Check className="h-10 w-10 stroke-[4px]" />
            </div>
            <h2 className="break-keep text-2xl font-black text-slate-900">
              {corepSignatureOnly
                ? "공동계약인 서명 이미지가 저장되었습니다."
                : "공동계약인 확인 및 서명이 완료되었습니다."}
            </h2>
            <p className="mt-3 break-keep text-base font-bold leading-7 text-slate-600">
              {corepSignatureOnly
                ? "관리자가 다시 출력하면 새로 등록한 서명 이미지가 계약서 출력물에 반영됩니다."
                : corepCompletedResult?.allCompleted
                ? "공동계약인 확인이 모두 완료되어 최종 계약서가 작성완료 처리되었습니다."
                : "아직 다른 공동계약인의 확인이 남아 있습니다. 모든 공동계약인 확인이 완료되면 최종 계약서가 작성완료 처리됩니다."}
            </p>
            <a href={`tel:${SUPPORT_PHONE.replace(/-/g, "")}`} className="mt-5 flex min-h-[52px] items-center justify-center rounded-2xl border border-slate-200 px-4 py-3 text-base font-black text-slate-700">
              문의 {SUPPORT_PHONE}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
